import React, { createContext, useContext, useEffect, useLayoutEffect, useState } from "react";

import { AuthContext } from "../Auth/AuthContext";

import api from "../../services/api";
import { getPublicFileUrl } from "../../config";
import { useThemeContext } from "../DarkMode";
import openSocket from "../../services/socket-io";
import { aplicarEstilo, hexParaHsl } from "../../helpers/estiloVisual";

const WhiteLabelContext = createContext({
  appName: "OrbeChat V.1",
  logoUrl: "",
  backgroundUrl: "",
  primaryColor: "",
  logoDarkUrl: "",
  iconUrl: "",
  faviconUrl: "",
  logoOnly: false,
  own: true,
  logoDoTema: "",
  iconeQuadrado: "",
  recarregar: () => {},
});

// Texto legível sobre a cor escolhida. Uma marca escura precisa de letra
// clara e vice-versa — fixar branco deixaria o botão ilegível em amarelo.
const contrasteDe = (hsl) => {
  const luminancia = Number(String(hsl).split(" ")[2]?.replace("%", "") || 50);
  return luminancia > 60 ? "222 47% 11%" : "0 0% 100%";
};

// Os arquivos do index.html. Quando a empresa não tem favicon próprio, é para
// cá que a aba volta — senão o ícone de uma empresa ficaria na aba depois de
// alguém limpar o campo.
const FAVICON_PADRAO = "/favicon.ico";
const APPLE_ICON_PADRAO = "/apple-touch-icon.png";

// O <link> do favicon é estático no index.html: o certo é trocar o href do que
// já existe. Criar um segundo deixa os dois valendo, e qual ganha depende do
// navegador.
const aplicarNoLink = (rel, href) => {
  let link = document.querySelector(`link[rel="${rel}"]`);

  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    document.head.appendChild(link);
  }

  link.href = href;
};

// A última marca e o último estilo lidos de cada empresa. No F5 a marca só é
// relida depois de a sessão voltar; sem isto, a tela aparece um instante com
// a cara da instalação e então troca para a da empresa.
const CACHE = "whiteLabel:";

const lerCache = (companyId) => {
  try {
    return JSON.parse(localStorage.getItem(CACHE + companyId));
  } catch {
    return null;
  }
};

const gravarCache = (companyId, valor) => {
  try {
    localStorage.setItem(CACHE + companyId, JSON.stringify(valor));
  } catch {
    // Sem localStorage (aba anônima, cota): só perde o atalho.
  }
};

export const WhiteLabelProvider = ({ children }) => {
  const { darkMode } = useThemeContext();
  // A empresa de quem está logado. A primeira leitura sai ANTES de a sessão
  // ser restaurada (não há token ainda) e volta com a marca da instalação —
  // é ela que a tela de entrar mostra. Quando o usuário aparece, a marca é
  // relida com o token, e aí vem a da empresa DELE.
  const { user } = useContext(AuthContext);
  const [marca, setMarca] = useState({
    appName: "OrbeChat V.1",
    logoUrl: "",
    backgroundUrl: "",
    primaryColor: "",
    logoDarkUrl: "",
    iconUrl: "",
    faviconUrl: "",
    logoOnly: false,
    own: true,
  });
  // Estilo visual escolhido pela empresa (null = padrão).
  const [estilo, setEstilo] = useState(null);
  // Estilo que o super admin está personalizando clicando nas páginas
  // (components/Aparencia). Enquanto existe, é ele que pinta a tela.
  const [rascunho, setRascunho] = useState(null);
  const [selecionado, setSelecionado] = useState(null);
  // Pré-visualizar: o rascunho continua pintando, mas a página volta a
  // receber clique de verdade (o inspetor recolhe).
  const [previa, setPrevia] = useState(false);
  const emEdicao = rascunho || estilo;

  const carregar = async () => {
    try {
      const { data } = await api.get("/settings/white-label");
      const lida = {
        appName: data?.appName || "OrbeChat V.1",
        logoUrl: data?.logoUrl || "",
        backgroundUrl: data?.backgroundUrl || "",
        primaryColor: data?.primaryColor || "",
        logoDarkUrl: data?.logoDarkUrl || "",
        iconUrl: data?.iconUrl || "",
        faviconUrl: data?.faviconUrl || "",
        logoOnly: !!data?.logoOnly,
        // Sem o campo (instalação antiga), a tela trabalha como sempre
        // trabalhou: marca própria liberada.
        own: data?.own !== false,
      };
      setMarca(lida);
      setEstilo(data?.estilo || null);
      if (user?.companyId) {
        gravarCache(user.companyId, { marca: lida, estilo: data?.estilo || null });
      }
    } catch (err) {
      // Sem whitelabel o sistema usa o visual padrão — não é motivo para
      // travar a tela.
    }
  };

  // Empresa nova na tela (a sessão voltou, ou trocou de conta): o que ficou
  // guardado dela entra já neste render, antes da primeira pintura — a
  // leitura do servidor confirma logo depois.
  const [empresaVista, setEmpresaVista] = useState(user?.companyId);
  if (user?.companyId !== empresaVista) {
    setEmpresaVista(user?.companyId);
    const salvo = user?.companyId && lerCache(user.companyId);
    if (salvo?.marca) {
      setMarca(salvo.marca);
      setEstilo(salvo.estilo || null);
    }
  }

  useEffect(() => {
    carregar();
    // Só o id da empresa: `user` é objeto novo a cada render do AuthContext e
    // reler a marca a cada render seria um pedido por render.
  }, [user?.companyId]);

  // Quem administra troca o estilo em Configurações: o servidor avisa a
  // empresa inteira, e cada tela aberta relê sem precisar de F5.
  useEffect(() => {
    if (!user?.id) return undefined;

    const socket = openSocket();
    socket.on("settings", (dados) => {
      if (dados?.setting?.key === "estiloVisual") carregar();
    });

    return () => socket.disconnect();
  }, [user?.id]);

  // Aplica no documento: título da aba, o estilo visual e a cor primária.
  // Na mesma ordem sempre — a cor da marca vem por último e vence o estilo.
  // Layout: as variáveis entram antes da pintura, sem um quadro no visual antigo.
  useLayoutEffect(() => {
    document.title = marca.appName;

    const raiz = document.documentElement;
    aplicarEstilo(raiz, emEdicao, darkMode);
    // Liga as regras de "clicar para escolher" do globals.css.
    raiz.toggleAttribute("data-editando-aparencia", !!rascunho && !previa);

    const hsl = hexParaHsl(marca.primaryColor);

    if (!hsl) return;

    raiz.style.setProperty("--primary", hsl);
    raiz.style.setProperty("--primary-foreground", contrasteDe(hsl));
    // O anel de foco acompanha a marca, senão fica um azul solto na tela.
    raiz.style.setProperty("--ring", hsl);
  }, [marca.appName, marca.primaryColor, emEdicao, rascunho, previa, darkMode]);

  const logoCompleta = getPublicFileUrl(marca.logoUrl);

  const fundoCompleto = getPublicFileUrl(marca.backgroundUrl);

  const logoEscuraCompleta = getPublicFileUrl(marca.logoDarkUrl);

  const iconeCompleto = getPublicFileUrl(marca.iconUrl);

  const faviconCompleto = getPublicFileUrl(marca.faviconUrl);

  // A logo da vez. Só troca quando existe a versão escura: sem ela, a clara
  // continua valendo — é melhor que um espaço vazio no menu.
  const logoDoTema = (darkMode && logoEscuraCompleta) || logoCompleta;

  // Espaço quadrado (menu recolhido, cabeçalho estreito): o símbolo, quando
  // houver. A logo deitada encolhida ali vira um borrão.
  const iconeQuadrado = iconeCompleto || logoDoTema;

  // Ícone da aba e do atalho na tela de início. O favicon é o arquivo próprio;
  // sem ele vale o SÍMBOLO da marca, que é quadrado por natureza e é o que
  // quase todo mundo sobe — a produção estava com logo e símbolo da empresa e a
  // aba continuava com o ícone de fábrica, porque só o campo "favicon"
  // alimentava a aba. O arquivo de fábrica é o último recurso.
  useEffect(() => {
    const favicon = faviconCompleto || iconeCompleto || FAVICON_PADRAO;

    aplicarNoLink("icon", favicon);
    aplicarNoLink("shortcut icon", favicon);
    aplicarNoLink(
      "apple-touch-icon",
      iconeCompleto || faviconCompleto || APPLE_ICON_PADRAO
    );
  }, [faviconCompleto, iconeCompleto]);

  return (
    <WhiteLabelContext.Provider
      value={{
        ...marca,
        logoUrl: logoCompleta,
        backgroundUrl: fundoCompleto,
        logoDarkUrl: logoEscuraCompleta,
        iconUrl: iconeCompleto,
        faviconUrl: faviconCompleto,
        logoDoTema,
        iconeQuadrado,
        recarregar: carregar,
        estilo: emEdicao,
        rascunho,
        setRascunho,
        selecionado,
        setSelecionado,
        previa,
        setPrevia,
      }}
    >
      {children}
    </WhiteLabelContext.Provider>
  );
};

export const useWhiteLabel = () => useContext(WhiteLabelContext);

export default WhiteLabelContext;
